import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApisService } from '@core/services/apis.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-edit-partners',
  templateUrl: './edit-partners.component.html',
  styleUrls: ['./edit-partners.component.scss']
})
export class EditPartnersComponent implements OnInit {
  
  partner: any = {
    id: '',
    name_ar: '',           // ✅ اسم الشريك بالعربي
    name_en: '',           // ✅ اسم الشريك بالإنجليزي
    url: '',
    order: '',
    logo: null as File | string | null,
    description_ar: '',
    description_en: '',
  };

  previewUrl: string | null = null;
  partnerId: string | null = null;
  isLoading: boolean = true;
  isSubmitting: boolean = false;

  constructor(
    private router: Router,
    private apisService: ApisService,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.partnerId = this.route.snapshot.paramMap.get('id');
    
    if (this.partnerId) {
      this.loadPartner(this.partnerId);
    } else {
      this.toastr.error(this.translate.instant('MENU.DASHBOARD.partner_id_not_found'));
      this.router.navigate(['/partners']);
    }
  }

  // ✅ دالة مساعدة: هل نعرض الشعار الحالي؟ (تجنب typeof في HTML)
  get showCurrentLogo(): boolean {
    return !!this.partner.logo && 
           typeof this.partner.logo === 'string' && 
           !this.previewUrl;
  }

  // ✅ تحميل بيانات الشريك مع دعم ثنائي اللغة
  loadPartner(id: string): void {
    this.isLoading = true;
    
    this.apisService.getPartnerById(id).subscribe({
      next: (res: any) => {
        const data = res?.data ? res.data : res;
        
        if (data) {
          // التعامل مع الاسم ثنائي اللغة
          this.partner = {
            id: data.id || '',
            name_ar: data.name_ar || data.name || '',
            name_en: data.name_en || data.name || '',
            url: data.url || '',
            order: data.order || '',
            logo: data.logo || null,
            description_ar: data.description_ar || data.description || '',
            description_en: data.description_en || data.description || '',
          };
        }
        
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load partner:', err);
        this.toastr.error(this.translate.instant('MENU.DASHBOARD.failed_to_load_partner'));
        this.isLoading = false;
      }
    });
  }

  // 🖼️ اختيار صورة الشعار
  onFileSelected(event: any): void {
    if (event.target.files?.length > 0) {
      const file = event.target.files[0];
      this.partner.logo = file;
      
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.previewUrl = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  // ❌ إزالة الصورة الحالية
  removeCurrentLogo(): void {
    this.partner.logo = null;
    this.previewUrl = null;
  }

  // ❌ إزالة الصورة الجديدة المختارة
  removeNewLogo(): void {
    this.previewUrl = null;
    if (!(this.partner.logo instanceof File)) {
      this.partner.logo = null;
    }
    const fileInput = document.getElementById('logoUpload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }

  // ✅ تحديث الشريك مع إرسال البيانات ثنائية اللغة
  updatePartner(): void {
    if (!this.partnerId) {
      this.toastr.error(this.translate.instant('MENU.DASHBOARD.partner_id_missing'));
      return;
    }

    // التحقق من الحقول المطلوبة
    if (!this.partner.name_ar || !this.partner.name_en || !this.partner.url) {
      this.toastr.error(this.translate.instant('MENU.DASHBOARD.fill_all_required_fields'));
      return;
    }

    this.isSubmitting = true;

    const formData = new FormData();
    
    // 📝 إرسال الاسم ثنائي اللغة
    formData.append('name_ar', this.partner.name_ar.trim());
    formData.append('name_en', this.partner.name_en.trim());
    
    formData.append('url', this.partner.url);
    formData.append('order', this.partner.order?.toString() || '0');
    
    // 📝 إرسال الوصف ثنائي اللغة
    formData.append('description_ar', this.partner.description_ar?.trim() || '');
    formData.append('description_en', this.partner.description_en?.trim() || '');

    // 🖼️ إرسال الشعار فقط إذا كان ملفاً جديداً
    if (this.partner.logo instanceof File) {
      formData.append('logo', this.partner.logo);
    }

    // 🗑️ إشارة لحذف الشعار إذا طُلب ذلك
    if (!this.partner.logo && !this.previewUrl && this.partner.logo !== null) {
      formData.append('remove_logo', 'true');
    }

    this.apisService.editPartners(this.partnerId, formData).subscribe({
      next: (response: any) => {
        this.isSubmitting = false;
        this.toastr.success(this.translate.instant('MENU.DASHBOARD.partner_updated_successfully'));
        setTimeout(() => {
          this.router.navigate(['/partners']);
        }, 1000);
      },
      error: (error: any) => {
        this.isSubmitting = false;
        console.error('Error updating partner:', error);
        
        if (error.error?.errors) {
          Object.keys(error.error.errors).forEach(key => {
            this.toastr.error(error.error.errors[key][0]);
          });
        } else {
          this.toastr.error(this.translate.instant('MENU.DASHBOARD.something_went_wrong'));
        }
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/partners']);
  }
}